import { GattinoniAnagraphicFormType } from 'types/window';
import {
    SupplierItem,
    ConstantsReturnObject,
    VatValidation,
    FiscalCodeValidation,
    PecValidation,
    SplitPaymentValidation,
} from 'types/data';
import axios, { AxiosRequestConfig } from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { verifyRecaptcha } from './recaptcha';
import { VAT_VALUE_EXCEPTION } from '@utils/constants';

declare global {
    interface Window {
        GattinoniAnagraphicForm: GattinoniAnagraphicFormType;
    }
}

// Data
export const suppliersTypesApi = `${window.GattinoniAnagraphicForm.REACT_APP_BASE_API_URL}/suppliers/types`;
export const constantsApi = `${window.GattinoniAnagraphicForm.REACT_APP_BASE_API_URL}/get_constants`;
export const saveDataApi = `${window.GattinoniAnagraphicForm.REACT_APP_BASE_API_URL}/handle-form`;

// Validations
export const validateVatApi = `${window.GattinoniAnagraphicForm.REACT_APP_BASE_API_URL}/validations/vat?country_code={country}&vat={vat}`;
export const validateFiscalCodeApi = `${window.GattinoniAnagraphicForm.REACT_APP_BASE_API_URL}/validations/fiscal_code?fiscal_code={fiscalCode}`;
export const validateSplitPaymentApi = `${window.GattinoniAnagraphicForm.REACT_APP_BASE_API_URL}/validations/split_payment?fiscal_code={fiscalCode}`;
export const validatePecApi = `${window.GattinoniAnagraphicForm.REACT_APP_BASE_API_URL}/validations/pec?pec={pec}`;

// API calls
export const getSuppliersTypes = (): Promise<SupplierItem[]> => {
    return new Promise((resolve, reject) => {
        axios
            .get(suppliersTypesApi)
            .then((value) => {
                if (value && value.data) {
                    resolve(value.data as SupplierItem[]);
                } else {
                    resolve([]);
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const getConstants = (): Promise<ConstantsReturnObject | undefined> => {
    return new Promise((resolve, reject) => {
        axios
            .get(constantsApi)
            .then((value) => {
                if (value && value.data) {
                    resolve(value.data as ConstantsReturnObject);
                } else {
                    resolve(undefined);
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};

export const postSaveData = async (data: any, recaptcha: ReCAPTCHA): Promise<any> => {
    const token = await verifyRecaptcha(recaptcha);
    if (token) {
        return new Promise((resolve, reject) => {
            const config: AxiosRequestConfig<any> = {
                headers: {
                    'Recaptcha-Token': token,
                },
            };
            axios
                .post(saveDataApi, data, config)
                .then(() => {
                    resolve('OK');
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
};

export const verifyVat = async (vat: string, countryCode: string, recaptcha: ReCAPTCHA) => {
    const token = await verifyRecaptcha(recaptcha);
    if (token) {
        return new Promise((resolve) => {
            const config: AxiosRequestConfig<any> = {
                headers: {
                    'Recaptcha-Token': token,
                },
            };

            axios
                .get<VatValidation[]>(
                    validateVatApi
                        .replace('{country}', countryCode === 'GR' ? 'EL' : countryCode)
                        .replace('{vat}', vat),
                    config,
                )
                .then((value) => {
                    if (value.status === 204) {
                        resolve(VAT_VALUE_EXCEPTION);
                    }
                    if (value.data.length > 0 && value.data[0].valid) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                })
                .catch(() => {
                    resolve(false);
                });
        });
    }
};

export const verifyFiscalCode = async (fiscalCode: string, recaptcha: ReCAPTCHA) => {
    const token = await verifyRecaptcha(recaptcha);
    if (token) {
        return new Promise((resolve) => {
            const config: AxiosRequestConfig<any> = {
                headers: {
                    'Recaptcha-Token': token,
                },
            };
            axios
                .get<FiscalCodeValidation>(validateFiscalCodeApi.replace('{fiscalCode}', fiscalCode), config)
                .then((value) => {
                    if (value.data.is_valid) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                })
                .catch(() => {
                    resolve(false);
                });
        });
    }
};

export const verifySplitPayment = async (fiscalCode: string, recaptcha: ReCAPTCHA) => {
    const token = await verifyRecaptcha(recaptcha);
    if (token) {
        return new Promise((resolve) => {
            const config: AxiosRequestConfig<any> = {
                headers: {
                    'Recaptcha-Token': token,
                },
            };
            axios
                .get<SplitPaymentValidation>(validateSplitPaymentApi.replace('{fiscalCode}', fiscalCode), config)
                .then((value) => {
                    if (value.status !== 200) {
                        resolve(false);
                    }
                    if (value.data.success) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                })
                .catch(() => {
                    resolve(false);
                });
        });
    }
};

export const verifyPec = async (pec: string, recaptcha: ReCAPTCHA) => {
    const token = await verifyRecaptcha(recaptcha);
    if (token) {
        return new Promise((resolve) => {
            const config: AxiosRequestConfig<any> = {
                headers: {
                    'Recaptcha-Token': token,
                },
            };
            axios
                .get<PecValidation>(validatePecApi.replace('{pec}', pec), config)
                .then((value) => {
                    //  !!OLD if (value.status === 200 && value.data.available) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    if (value.status === 200) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                })
                .catch(() => {
                    resolve(false);
                });
        });
    }
};
